// Videos page: featured-videos-2cv and video-series-2cv.
//
// Every video is a plain link to YouTube with a thumbnail image in it, so
// the page loads no player and no YouTube script. Pressing play swaps in a
// youtube-nocookie.com player that starts straight away. Ctrl/cmd/middle
// clicks, and browsers without JavaScript, just follow the link.

(() => {
  const isPlainClick = (event) =>
    event.button === 0 && !event.metaKey && !event.ctrlKey && !event.shiftKey && !event.altKey;

  const prefersReducedMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const videoFrom = (link) => ({
    id: link.dataset.videoId,
    title: link.dataset.videoTitle || '',
    details: link.dataset.videoDetails || '',
    series: link.dataset.videoSeries || '',
    partsUrl: link.dataset.videoPartsUrl || '',
    partsLabel: link.dataset.videoPartsLabel || '',
  });

  const createPlayer = (video) => {
    const iframe = document.createElement('iframe');
    iframe.className = 'videos-2cv__iframe';
    iframe.src = `https://www.youtube-nocookie.com/embed/${encodeURIComponent(video.id)}?autoplay=1&rel=0&playsinline=1`;
    iframe.title = video.title || 'YouTube video';
    iframe.allow = 'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    iframe.referrerPolicy = 'strict-origin-when-cross-origin';
    iframe.allowFullscreen = true;
    return iframe;
  };

  // Brings an element into view below the sticky nav bar, if it isn't
  // fully visible already.
  const reveal = (element) => {
    const headerHeight = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
    const rect = element.getBoundingClientRect();
    if (rect.top >= headerHeight && rect.bottom <= window.innerHeight) return;

    window.scrollTo({
      top: window.scrollY + rect.top - headerHeight - 16,
      behavior: prefersReducedMotion() ? 'auto' : 'smooth',
    });
  };

  class FeaturedVideos2cv extends HTMLElement {
    connectedCallback() {
      this.player = this.querySelector('[data-player]');
      this.onClick = this.onClick.bind(this);
      this.addEventListener('click', this.onClick);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
    }

    onClick(event) {
      const link = event.target.closest('a[data-video-id]');
      if (!link || !isPlainClick(event)) return;

      event.preventDefault();
      this.play(videoFrom(link));
    }

    // Also called by the series sections below, to play their videos here.
    play(video) {
      if (!this.player) return;

      const iframe = createPlayer(video);
      this.player.replaceChildren(iframe);
      iframe.focus({ preventScroll: true });
      this.showCaption(video);

      this.querySelectorAll('.featured-videos-2cv__item').forEach((item) => {
        if (item.dataset.videoId === video.id) {
          item.setAttribute('aria-current', 'true');
        } else {
          item.removeAttribute('aria-current');
        }
      });

      document.dispatchEvent(new CustomEvent('videos2cv:play', { detail: { id: video.id } }));
    }

    showCaption(video) {
      const setText = (selector, text) => {
        const element = this.querySelector(selector);
        if (!element) return;
        element.textContent = text;
        element.hidden = !text;
      };

      setText('[data-caption-series]', video.series);
      setText('[data-caption-title]', video.title);
      setText('[data-caption-details]', video.details);

      const parts = this.querySelector('[data-caption-parts]');
      if (parts) {
        parts.hidden = !video.partsUrl;
        if (video.partsUrl) {
          parts.href = video.partsUrl;
          parts.querySelector('[data-caption-parts-label]').textContent = video.partsLabel;
        }
      }
    }
  }

  class VideoSeries2cv extends HTMLElement {
    connectedCallback() {
      this.onClick = this.onClick.bind(this);
      this.onPlay = this.onPlay.bind(this);
      this.addEventListener('click', this.onClick);
      document.addEventListener('videos2cv:play', this.onPlay);
    }

    disconnectedCallback() {
      this.removeEventListener('click', this.onClick);
      document.removeEventListener('videos2cv:play', this.onPlay);
    }

    onClick(event) {
      const seeAll = event.target.closest('[data-see-all]');
      if (seeAll) {
        this.toggleAll(seeAll);
        return;
      }

      const link = event.target.closest('a[data-video-id]');
      if (!link || !isPlainClick(event)) return;

      event.preventDefault();
      const video = videoFrom(link);
      const featured = document.querySelector('featured-videos-2cv');

      if (featured && featured.player) {
        featured.play(video);
        reveal(featured.player);
      } else {
        this.playInCard(link, video);
      }
    }

    // A page without the featured player: play in the card's own
    // thumbnail spot. The player sits beside the link (a link can't hold
    // an iframe), and the link's thumbnail is hidden while it plays.
    playInCard(link, video) {
      const card = link.closest('.video-series-2cv__card');
      let box = card.querySelector('.video-series-2cv__inline-player');
      if (!box) {
        box = document.createElement('div');
        box.className = 'video-series-2cv__thumb video-series-2cv__inline-player';
        link.before(box);
        link.querySelector('.video-series-2cv__thumb').hidden = true;
      }

      const iframe = createPlayer(video);
      box.replaceChildren(iframe);
      iframe.focus({ preventScroll: true });
    }

    onPlay(event) {
      this.querySelectorAll('.video-series-2cv__card').forEach((card) => {
        card.classList.toggle('is-now', card.dataset.videoId === event.detail.id);
      });
    }

    toggleAll(button) {
      const expand = button.getAttribute('aria-expanded') !== 'true';
      button.setAttribute('aria-expanded', expand);
      button.textContent = expand ? button.dataset.lessLabel : button.dataset.moreLabel;
      this.classList.toggle('is-expanded', expand);
    }
  }

  if (!customElements.get('featured-videos-2cv')) {
    customElements.define('featured-videos-2cv', FeaturedVideos2cv);
  }

  if (!customElements.get('video-series-2cv')) {
    customElements.define('video-series-2cv', VideoSeries2cv);
  }
})();
